import React, { useEffect, useState } from 'react';
import { gameApi, PlayerAchievementProgress } from '../api';
import { AchievementCard } from './AchievementCard';

interface AchievementGroup {
  group: string;
  achievements: PlayerAchievementProgress[];
}

const groupAchievements = (
  achievements: PlayerAchievementProgress[]
): AchievementGroup[] => {
  const byGroup = new Map<string, PlayerAchievementProgress[]>();

  achievements.forEach((achievement) => {
    const group = achievement.group ?? '';
    const grouped = byGroup.get(group);
    if (grouped) {
      grouped.push(achievement);
    } else {
      byGroup.set(group, [achievement]);
    }
  });

  return Array.from(byGroup.entries())
    .sort(([left], [right]) => {
      if (left === right) {
        return 0;
      }

      if (left === '') {
        return -1;
      }

      if (right === '') {
        return 1;
      }

      return left.localeCompare(right);
    })
    .map(([group, groupAchievements]) => ({
      group,
      achievements: [...groupAchievements].sort((a, b) => a.name.localeCompare(b.name)),
    }));
};

export const AchievementsView: React.FC = () => {
  const [achievements, setAchievements] = useState<PlayerAchievementProgress[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadAchievements = async () => {
      setLoading(true);
      setError(null);

      try {
        const payload = await gameApi.getPlayerAchievements();
        if (cancelled) {
          return;
        }

        setAchievements(payload);
      } catch (err) {
        if (cancelled) {
          return;
        }

        setError(err instanceof Error ? err.message : 'Failed to load achievements');
        setAchievements([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAchievements();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="home-menu achievements-view">
      <h1>Achievements</h1>
      {loading && <div role="status">Loading achievements...</div>}
      {error && <div className="error" role="alert">{error}</div>}
      {!loading && !error && <p className="achievements-summary">
        {achievements.filter(achievement => achievement.isReceived).length} of {achievements.length} achievements earned.
      </p>}
      {!loading && !error && achievements.length === 0 && <div>No achievements found.</div>}

      {!loading && !error && achievements.length > 0 && (
        <>
          {groupAchievements(achievements).map((achievementGroup) => (
            <section className="achievements-group" key={achievementGroup.group || 'base'}>
              <h2 className="achievements-group-title">{achievementGroup.group || 'Base game'}</h2>
              <div className="achievements-grid">
                {achievementGroup.achievements.map((achievement) => (
                  <AchievementCard
                    key={achievement.name}
                    achievement={achievement}
                    isReceived={!!achievement.isReceived}
                    presentation="collection"
                  />
                ))}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
};
